/**
 * Centralised access-control constants for the simulator.
 *
 * The platform-level rule, mirrored across the web SPA and the mobile
 * apps: only goastern.com users with a paid `RADAR_SIM_FULL_SKU`
 * entitlement get the full scenario library. Everyone else (anonymous
 * web visitor, signed-in user who hasn't paid) gets a single demo
 * scenario to try out the simulator.
 *
 * Payment is gated to iOS / Android — Apple StoreKit and Google Play
 * Billing receipts feed `auth.goastern.com /entitlements/verify`, which
 * grants the SKU. The web build cannot create accounts or take payment
 * itself; existing accounts can sign in to use what they've bought.
 *
 * Changing these IDs requires a coordinated update in:
 *   - apps/web/src/access.ts (this file)
 *   - apps/web/src/marketing/Home.tsx (CTAs)
 *   - apps/web/src/App.tsx (scenario filter)
 *   - the App Store / Play Console product IDs that map to the SKU
 *   - goastern-auth's product registration (if any)
 */

/** Entitlement SKU granted when a user buys the simulator via the
 *  Apple App Store or Google Play. Same string is used on both
 *  platforms so a single entitlement row in goastern-auth covers a
 *  cross-platform purchase. */
export const RADAR_SIM_FULL_SKU = "goastern.radar.sim.full" as const;

/** Scenario shown without a paid entitlement. Picked for being the
 *  shortest, simplest scenario in the library — head-up vs north-up
 *  practice in the central Solent with one tracked yacht. Enough to
 *  show what the simulator does without giving the entire course
 *  away. */
export const FREE_DEMO_SCENARIO_ID = "g34-003-familiarisation-headup-northup" as const;

export interface EntitlementSnapshot {
  /** True if the user is signed in to goastern.com. */
  authenticated: boolean;
  /** SKUs the user has been granted (verified by goastern-auth). */
  skus: ReadonlyArray<string>;
}

/** Single rule for "may this user pick any scenario, or just the
 *  demo?". Anonymous → demo only. Signed-in without the paid SKU →
 *  demo only. Signed-in with the SKU → unlocked. */
export function hasFullAccess(snap: EntitlementSnapshot): boolean {
  return snap.skus.includes(RADAR_SIM_FULL_SKU);
}
